import {
  computed,
  signal,
  Signal,
  untracked,
  WritableSignal,
} from "@angular/core";
import {
  EntityMap,
  EntityMetadata,
  EntityStoreState,
  EntityStoreStateBase,
  Entries,
  Identifiable,
} from "../store-types.model";
import { StoreService } from "../store/store.service";
import { initMethodsStatus } from "../utils/store-utils.service";
import { EntityFacadeService } from "./entity-facade.service";

/**
 * Service class for managing entities in a store.
 *
 * @template TEntity - The type of entities managed by the store. Must extend Identifiable.
 * @template TEntity - The type of Metadata in each entity.
 * @template TState - The type of state managed by the store. Must extend StoreState.
 */
export class EntityStoreService<
  TEntity extends Identifiable = Identifiable,
  TMetadata extends EntityMetadata = EntityMetadata,
  TState extends EntityStoreStateBase = EntityStoreState,
> extends StoreService<TState> {
  // State
  protected _entities: WritableSignal<EntityMap<TEntity>>;

  protected _metadatas: WritableSignal<EntityMap<TMetadata>>;

  public static initialState = {
    loading: 0,
    error: null,
  };

  // Selectors
  public entities: Signal<Signal<TEntity>[]>;
  public allEntities: Signal<TEntity[]>;
  public loading: Signal<number>;

  constructor(initialState: TState) {
    super(initialState);
    this._entities = signal<EntityMap<TEntity>>({});
    this._metadatas = signal<EntityMap<TMetadata>>({});
    this.allEntities = computed(() =>
      Object.values(this._entities()).map((e) => e()).filter((e): e is TEntity => e !== null)
    );
    this.entities = computed<Signal<TEntity>[]>(() => {
      return Object.values(this._entities()).filter(
        (e) => untracked(() => e()) !== null,
      );
    });
    this.loading = computed<number>(() => this._state.loading());
  }

  public incrementLoading() {
    this.patchState({
      loading: this._state.loading() + 1,
    });
  }

  public decrementLoading() {
    this.patchState({
      loading: this._state.loading() - 1,
    });
  }

  /**
   * Retrieves the entity signal with ID. If the entity does not exist, creates a weak signal for it.
   * untracked entities list
   *
   * @public
   * @param {string} id - The ID of the entity to retrieve.
   * @returns {Signal<TEntity>} The signal associated with the entity.
   * @throws {Error} Throws an error if the provided ID is null.
   */
  public getById(id: string): Signal<TEntity> {
    if (!id) {
      return null;
    }
    const found = untracked(() => this._entities())[id];
    if (found) {
      return found.asReadonly();
    } else {
      const signalEntity = signal(null as TEntity);
      untracked(() =>
        this._entities.set({ ...this._entities(), [id]: signalEntity }),
      );
      return signalEntity;
    }
  }

  /**
   * Adds multiple entities to the state.
   *
   * @public
   * @param {T[]} entities - The array of entities to add to the state.
   */
  public addMany(entities: TEntity[]) {
    if (!entities?.length) return;

    const currentEntities = this._entities();
    const currentMetadatas = untracked(() => this._metadatas());
    const newEntries: Record<string, WritableSignal<TEntity>> = {};
    const newMetaEntries: Record<string, WritableSignal<TMetadata>> = {};
    let hasNew = false;

    for (const entity of entities) {
      if (!entity) continue;
      if (currentEntities.hasOwnProperty(entity.id)) {
        // Existing: update signal value in-place (no map change)
        currentEntities[entity.id].set(entity);
      } else {
        // New: collect for single batch insert
        newEntries[entity.id] = signal(entity);
        newMetaEntries[entity.id] = signal({} as TMetadata);
        hasNew = true;
      }
    }

    if (hasNew) {
      untracked(() => {
        this._entities.set({ ...currentEntities, ...newEntries });
        this._metadatas.set({ ...currentMetadatas, ...newMetaEntries });
      });
    }
  }

  public isStored(id: string) {
    return this._entities().hasOwnProperty(id);
  }

  /**
   * Upserts a single entity into the state. If the entity already exists, it updates it; otherwise, it adds it.
   *
   * @param {TEntity} entity - The entity to upsert.
   * @param {Partial<TMetadata>} [metadata] - optional - The partial metadata to associate with the entity.
   * @returns {Signal<TEntity>} The signal of entity.
   */
  public upsertOne(
    entity: TEntity,
    metadata?: Partial<TMetadata> | Object,
  ): Signal<TEntity> {
    if (!entity) {
      return;
    }
    if (!this._entities().hasOwnProperty(entity.id)) {
      return this.addOne(entity, metadata);
    }
    return this.updateOne(entity, metadata);
  }

  /**
   * Updates single entity to the state.
   *
   * @param {TEntity} entity - The entity to update in the state.
   * @param {Partial<TMetadata>} [metadata] - The partial metadata to update for the entity.
   * @returns {Signal<TEntity>} The signal associated with the updated entity.
   * @throws {Error} Throws an error if the entity does not exist in the state.
   */
  public updateOne(
    entity: TEntity,
    metadata?: Partial<TMetadata> | Object,
  ): Signal<TEntity> {
    if (!this._entities().hasOwnProperty(entity.id)) {
      throw new Error(`entity id ${entity.id} don't exists in state`);
    }
    const entitySignal = this._entities()[entity.id];
    entitySignal.set(entity);
    if (metadata) {
      this.updateMetadata(entity.id, metadata);
    }
    return entitySignal.asReadonly();
  }

  /**
   * Adds a single entity to the state.
   *
   * @param {TEntity} entity - The entity to add to the state.
   * @param {Partial<TMetadata>} [metadata] - The partial metadata of the entity.
   * @returns {Signal<TEntity>} The signal of the added entity.
   * @throws {Error} Throws an error if the entity already exists in the state.
   */
  public addOne(
    entity: TEntity,
    metadata?: Partial<TMetadata> | Object,
  ): Signal<TEntity> {
    if (this._entities().hasOwnProperty(entity.id)) {
      const existing = this._entities()[entity.id];
      if (untracked(() => existing()) === null) {
        existing.set(entity);
        this.createMetadata(entity.id, metadata);
        return existing.asReadonly();
      }
      throw new Error(`entity id: ${entity.id} already exists`);
    }
    const signalEntity = signal(entity);
    untracked(() =>
      this._entities.set({ ...this._entities(), [entity.id]: signalEntity }),
    );
    this.createMetadata(entity.id, metadata);
    return signalEntity.asReadonly();
  }

  /**
   * Removes an entity from the state by ID.
   *
   * @param {string} id - The ID of the entity to remove.
   */
  public removeOne(id: string) {
    const updatedEntityState = { ...this._entities() };
    let removedEntity: WritableSignal<TEntity>;
    if (updatedEntityState.hasOwnProperty(id)) {
      removedEntity = updatedEntityState[id];
      delete updatedEntityState[id];
    } else {
      console.error(`removed id don't exists`);
    }
    this._entities.set(updatedEntityState);
    removedEntity?.set(null);
  }

  /**
   * Removes all entities from store
   *
   */
  public removeAll() {
    this._entities.set({});
    this._metadatas.set({});
  }

  /**
   *   METATADATA
   */

  /**
   * Retrieves the metadata signal with entity ID.
   * untrack the list of entities
   *
   * @param {string} entityId - The ID of the entity.
   * @returns {Signal<TMetadata>} The signal containing the metadata of the entity.
   * @throws {Error} Throws an error if the entity does not exist.
   */
  getMetadata(entityId: string): WritableSignal<TMetadata> {
    const metadatas = untracked(() => this._metadatas());
    if (metadatas.hasOwnProperty(entityId)) {
      return metadatas[entityId];
    } else {
      const metadata = signal({} as TMetadata);
      untracked(() =>
        this._metadatas.set({ ...this._metadatas(), [entityId]: metadata }),
      );
      return metadata;
    }
  }

  /**
   * Updates the metadata of the entity with the provided ID.
   *
   * @param {string} id - The ID of the entity whose metadata needs to be updated.
   * @param {Partial<TMetadata>} metadata - The partial metadata to update.
   * @returns {WritableSignal<TMetadata>} The writable signal containing metadatas.
   * @throws {Error} Throws an error if the entity does not exist.
   */
  updateMetadata(
    id: string,
    metadata: Partial<TMetadata> | Object,
  ): WritableSignal<TMetadata> {
    const signalMetadata = this.getMetadata(id);
    signalMetadata.update((m) => ({ ...m, ...metadata }));
    return signalMetadata;
  }

  createMetadata(
    id: string,
    metadata: Partial<TMetadata> | Object,
  ): WritableSignal<TMetadata> {
    const metaDataSignal = this.getMetadata(id);
    metaDataSignal.update((m) => ({ ...m, ...metadata }));
    return metaDataSignal as WritableSignal<TMetadata>;
  }

  /**
   * Creates a WeakRef of signal by id
   * if exists and not cleaned by garbage collector it was returned
   *
   * @param {string} id - The ID of the entity associated with the weak signal.
   * @returns {Signal<TEntity>} The weak signal created and associated with the ID.
   */

  initStatus(type) {
    const statusState = initMethodsStatus(type.prototype ?? type, false);
    const state = Object.fromEntries(
      (Object.entries(statusState) as Entries<TState>[]).map(
        ([key, value]: [keyof TState, any]) => {
          return [key, signal(value)];
        },
      ),
    );

    const entityFacadeService = EntityFacadeService;
    const statusEntityState = initMethodsStatus(
      entityFacadeService.prototype,
      false,
    );
    const entityState = Object.fromEntries(
      (Object.entries(statusEntityState) as Entries<TState>[]).map(
        ([key, value]: [keyof TState, any]) => {
          return [key, signal(value)];
        },
      ),
    );
    this._state = { ...this._state, ...state, ...entityState };
  }
}
